import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  getBootFallbackMessage,
  isBootWindowActive,
  isChunkOrHydrationBootError,
  logBootFailure,
  tryBootReloadOnce,
} from './bootRuntime';

class RouteBootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      pathname: props.pathname,
    };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.pathname !== state.pathname) {
      return {
        error: null,
        pathname: props.pathname,
      };
    }

    return null;
  }

  componentDidCatch(error, errorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return this.props.renderError?.(this.state.error) ?? null;
    }

    return this.props.children;
  }
}

function BootFallbackCard({ pathname, reload }) {
  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 440,
          width: '100%',
          borderRadius: 4,
          p: 3,
          boxShadow: '0 16px 40px rgba(19,62,135,0.12)',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', mb: 1.5 }}>
          Page failed to load
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>{getBootFallbackMessage(pathname)}</Typography>
        <Button
          type="button"
          variant="contained"
          onClick={reload}
          sx={{ borderRadius: '999px', px: 3, py: 1.25, fontWeight: 800, textTransform: 'none' }}
        >
          Retry
        </Button>
      </Paper>
    </Box>
  );
}

export default function BootRouteBoundary({
  children,
  storage = typeof window !== 'undefined' ? window.sessionStorage : null,
  reload = typeof window !== 'undefined' ? () => window.location.reload() : () => {},
  isBootWindowActiveFn = isBootWindowActive,
  pathnameOverride = '',
}) {
  const location = useLocation();
  const pathname = pathnameOverride || location.pathname;
  const [fallbackError, setFallbackError] = useState(null);
  const [reloadTriggered, setReloadTriggered] = useState(false);

  useEffect(() => {
    setFallbackError(null);
    setReloadTriggered(false);
  }, [pathname]);

  const handleError = useCallback(
    (error) => {
      const isBootActive = isBootWindowActiveFn();
      const isRecoverableBootError = isBootActive && isChunkOrHydrationBootError(error);

      if (isRecoverableBootError) {
        logBootFailure({ error, pathname, source: 'route-boundary-reload' });
      }

      if (isRecoverableBootError && tryBootReloadOnce({ pathname, storage, reload })) {
        setReloadTriggered(true);
        return;
      }

      logBootFailure({ error, pathname, source: 'route-boundary' });
      setReloadTriggered(false);
      setFallbackError(error);
    },
    [isBootWindowActiveFn, pathname, reload, storage],
  );

  const renderError = useMemo(
    () => (error) => {
      if (reloadTriggered) {
        return null;
      }

      return <BootFallbackCard pathname={pathname} reload={reload} error={fallbackError || error} />;
    },
    [fallbackError, pathname, reload, reloadTriggered],
  );

  return (
    <RouteBootErrorBoundary pathname={pathname} onError={handleError} renderError={renderError}>
      {children}
    </RouteBootErrorBoundary>
  );
}
